/**
 * Utility functions for the ManuallyAddPlayerModal component.
 *
 * Pure functions with no React dependencies — created once at module level
 * and reusable across components and tests.
 * @module components/ManuallyAddPlayerModal/utils
 */

import { isValid, parse, isFuture } from "date-fns";

import {
  IManuallyAddPlayerForm,
  ManualPlayerEditData,
} from "@/types/manuallyAddPlayerModalTypes";

/** Reference date for date-fns parse — epoch is fine since we only check validity */
const REFERENCE_DATE = new Date(0);

/**
 * Attempts to parse a date string in either ISO (YYYY-MM-DD) or
 * US (mm/dd/yyyy) format and returns a valid Date or null.
 * @param value - Date string to parse
 * @returns Parsed Date if valid, null otherwise
 */
const parseDate = (value: string): Date | null => {
  if (!value) return null;

  const isoResult = parse(value, "yyyy-MM-dd", REFERENCE_DATE);
  if (isValid(isoResult)) return isoResult;

  const usResult = parse(value, "MM/dd/yyyy", REFERENCE_DATE);
  if (isValid(usResult)) return usResult;

  return null;
};

/**
 * Checks whether a string represents a valid date in ISO or mm/dd/yyyy format.
 * Rejects partial dates, out-of-range days/months, and invalid leap year dates.
 * @param value - Date string to validate
 * @returns true when the string is a real calendar date
 */
export const isValidDateString = (value: string): boolean =>
  parseDate(value) !== null;

/**
 * Checks whether a date string represents a date in the future.
 * Returns false for invalid or empty strings.
 * @param value - Date string to check
 * @returns true when the date is after today
 */
export const isFutureDate = (value: string): boolean => {
  const parsed = parseDate(value);

  return parsed !== null && isFuture(parsed);
};

/**
 * Splits a search string into first name and last name parts
 * for pre-filling the manual player form.
 *
 * Single word goes entirely to firstName.
 * Multiple words split at the first space boundary.
 * @param searchText - Raw search text from a search bar
 * @returns Object with firstName and lastName strings
 */
/**
 * Extracts the team name from a display label that may include a league suffix.
 * Team autocomplete labels use the format "Team Name - League Name".
 * Returns undefined for empty strings so the mutation omits the field.
 * @param displayLabel - Raw currentTeam value from the form (e.g. "Aston Villa - Premier League")
 * @returns Team name without league suffix, or undefined if empty
 */
export const extractTeamName = (
  displayLabel: string | undefined
): string | undefined => {
  const trimmed = (displayLabel ?? "").trim();
  if (!trimmed) return undefined;

  const separatorIndex = trimmed.indexOf(" - ");

  return separatorIndex === -1 ? trimmed : trimmed.slice(0, separatorIndex);
};

export const splitSearchText = (
  searchText: string | undefined
): { firstName: string; lastName: string } => {
  const trimmed = (searchText ?? "").trim();
  const spaceIndex = trimmed.indexOf(" ");

  return spaceIndex === -1
    ? { firstName: trimmed, lastName: "" }
    : {
        firstName: trimmed.slice(0, spaceIndex),
        lastName: trimmed.slice(spaceIndex + 1).trim(),
      };
};

/**
 * Compares sorted string arrays for equality regardless of element order.
 * @param a - First array
 * @param b - Second array
 * @returns true when both arrays contain the same elements
 */
const arraysEqualUnordered = (
  a: readonly string[],
  b: readonly string[]
): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x.localeCompare(y));
  const sortedB = [...b].sort((x, y) => x.localeCompare(y));

  return sortedA.every((val, idx) => val === sortedB[idx]);
};

/**
 * Builds a mutation input containing only fields that actually changed from the original editData.
 * Ensures the backend's updatedFields metadata accurately reflects what the user modified,
 * rather than logging every field on every save.
 * @param formData - Current form values from react-hook-form
 * @param editData - Original values the form was pre-filled with
 * @returns Partial input with only changed fields (may be empty if nothing changed)
 */
export const buildChangedFieldsInput = (
  formData: IManuallyAddPlayerForm,
  editData: ManualPlayerEditData
): Record<string, string | string[] | undefined> => {
  const trimmedFirst = formData.firstName.trim();
  const trimmedLast = formData.lastName.trim();
  const newTeamName = extractTeamName(formData.currentTeam);
  const originalTeamName = extractTeamName(editData.currentTeam);
  const newNationality = formData.nationalityId || undefined;
  const originalNationality = editData.nationalityId || undefined;
  const newDob = formData.dateOfBirth || undefined;
  const originalDob = editData.dateOfBirth || undefined;

  return {
    ...(trimmedFirst !== editData.firstName && { firstName: trimmedFirst }),
    ...(trimmedLast !== editData.lastName && { lastName: trimmedLast }),
    ...(newTeamName !== originalTeamName && { teamName: newTeamName }),
    ...(!arraysEqualUnordered(formData.positionIds, editData.positionIds) && {
      positionIds: formData.positionIds,
    }),
    ...(newNationality !== originalNationality && {
      nationalityId: newNationality,
    }),
    ...(newDob !== originalDob && { dateOfBirth: newDob }),
  };
};
